import React, {useMemo} from "react";
import "./createEditForm.sass";
import {useTranslation} from "react-i18next";
import Tag from "../../atoms/tag/tag";
import Breadcrumb, {BreadcrumbItem} from "../../atoms/breadcrumb/breadcrumb";
import {IconSource} from "../../../shared/enums/iconSource";
import {Color} from "../../../shared/enums/color";
import HaloIcon from "../haloIcon/haloIcon";
import HeaderColor from "../../atoms/headerColor/headerColor";

/**
 * Properties that define a CreateEditFormHeader.
 */
export interface ICreateEditFormHeaderProps {
  /**
   * True if there are unsaved changes.
   */
  hasUnsavedChanges?: boolean;
  breadcrumbItem?: BreadcrumbItem;
  iconSource?: IconSource;
  haloColor?: Color;
}

/**
 * A form template with header and body sections used for Admin Portal's create and edit operations.
 */
const CreateEditForm: React.FC & {
  Header: React.FC<ICreateEditFormHeaderProps>;
} & {Body: React.FC} = ({children}) => {
  const subComponentsList = useMemo(() => Object.keys(CreateEditForm), []);

  const createEditFormChildren = subComponentsList.map((key) => {
    return React.Children.map(
      children,
      //@ts-ignore
      (child) => ((child?.type?.displayName ?? "") === key ? child : null)
    );
  });

  return <div className="card">{createEditFormChildren}</div>;
};

/**
 * The header section of CreateEditForm.
 */
const Header: React.FC<ICreateEditFormHeaderProps> = ({
  children,
  hasUnsavedChanges,
  breadcrumbItem,
  iconSource,
  haloColor
}) => {
  const {t} = useTranslation(["translation"]);

  return (
    <div className="sticky-top">
      {haloColor && <HeaderColor color={haloColor} />}
      <div className={"create-edit-form-header card-header"}>
        <h5>
          {iconSource && haloColor && (
            <HaloIcon iconSource={iconSource} haloColor={haloColor} />
          )}
          {breadcrumbItem && <Breadcrumb item={breadcrumbItem}></Breadcrumb>}
          {hasUnsavedChanges && (
            <Tag
              type="system"
              text={t("translation:labels.unsavedChanges")}
            ></Tag>
          )}
        </h5>
        <div>
          <>{children}</>
        </div>
      </div>
    </div>
  );
};
CreateEditForm.Header = Header;
CreateEditForm.Header.displayName = "Header";

/**
 * The body section of CreateEditForm.
 */
const Body: React.FC = ({children}) => {
  return <div className="card-body">{children}</div>;
};
CreateEditForm.Body = Body;
CreateEditForm.Body.displayName = "Body";

export default CreateEditForm;
